import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Badge, Field, SectionTitle } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import { Modal } from "@/pages/Contracts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Plus, MapPin, Plane } from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

const URG_TONE = { critical: "red", urgent: "orange", standard: "cyan" };
const URG_LABEL = { critical: "CRITICAL AOG", urgent: "AOG URGENT", standard: "MAINT REQ" };

export default function AOG() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState({});
  const [form, setForm] = useState({ title: "", aircraft_type: "", tail_number: "", location: "", issue: "", urgency: "urgent", contact: "" });

  const load = () => api.get("/aog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/aog", form);
      toast.success("AOG posted — mechanics notified");
      setOpen(false);
      setForm({ title: "", aircraft_type: "", tail_number: "", location: "", issue: "", urgency: "urgent", contact: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <BackToCockpit />
      <div className="flex items-end justify-between mb-8">
        <SectionTitle overline="AOG RESPONSE" title="Aircraft on ground." subtitle="Critical alerts routed to qualified A&P mechanics in the network." />
        {user && (
          <button data-testid="btn-post-aog" onClick={() => setOpen(true)} className="btn-hud btn-hud-orange">
            <Plus className="h-3.5 w-3.5" /> REPORT AOG
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5 stagger">
        {items.map((a) => (
          <Card key={a.id} testId={`aog-${a.id}`} className={a.urgency === 'critical' ? 'border-aog/60' : ''}>
            <div className="flex items-start justify-between mb-4">
              <Badge tone={URG_TONE[a.urgency]} pulse={a.urgency !== 'standard'}>
                <AlertTriangle className="h-3 w-3" /> {URG_LABEL[a.urgency]}
              </Badge>
              <div className="overline">{a.posted_by_name}</div>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-3">{a.title}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 font-mono text-orange-hud"><Plane className="h-3.5 w-3.5" /> {a.aircraft_type} {a.tail_number && `· ${a.tail_number}`}</div>
              <div className="flex items-center gap-2 font-mono text-slate-200"><MapPin className="h-3.5 w-3.5 text-cyan" /> {a.location}</div>
            </div>
            <p className="mt-4 text-sm text-slate-300">{a.issue}</p>
            {a.contact && <div className="mt-5 overline">CONTACT: {a.contact}</div>}
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-slate-500 overline">NO ACTIVE AOG — ALL BIRDS FLYING</div>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h2 className="font-display text-2xl font-bold mb-6">Report AOG</h2>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Title"><input data-testid="input-aog-title" required className="input-hud" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Aircraft Type">
                <select
                  data-testid="input-aog-aircraft"
                  required
                  className="input-hud"
                  value={form.aircraft_type}
                  onChange={(e) => setForm({ ...form, aircraft_type: e.target.value })}
                >
                  <option value="">Select aircraft…</option>
                  {Object.entries(catalog)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([oem, list]) => (
                      <optgroup key={oem} label={oem}>
                        {[...list]
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((t) => <option key={t.code} value={t.label}>{t.label}</option>)}
                      </optgroup>
                    ))}
                </select>
              </Field>
              <Field label="Tail Number"><input data-testid="input-aog-tail" className="input-hud" value={form.tail_number} onChange={(e) => setForm({ ...form, tail_number: e.target.value })} /></Field>
              <Field label="Location">
                <AirportInput
                  testId="input-aog-loc"
                  value={form.location}
                  onChange={(v) => setForm({ ...form, location: v })}
                  placeholder="Type city or ICAO (KTEB, Miami…)"
                />
              </Field>
              <Field label="Urgency">
                <select data-testid="input-aog-urg" className="input-hud" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                  <option value="critical">Critical</option>
                  <option value="urgent">Urgent</option>
                  <option value="standard">Standard</option>
                </select>
              </Field>
            </div>
            <Field label="Issue Description"><textarea data-testid="input-aog-issue" required rows={4} className="input-hud" value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} /></Field>
            <Field label="Contact"><input data-testid="input-aog-contact" className="input-hud" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
            <button data-testid="btn-submit-aog" className="btn-hud btn-hud-orange w-full justify-center">DISPATCH ALERT</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
